import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { State } from "../state";
import Seo from "./Seo";
import Testimonials from "./testimonials/Testimonials";
import { trackPricingCtaClick } from "../util/analytics";
import { PRODUCT_FACTS } from "../content/productFacts";
import { ORGANIZATION_ID, SOFTWARE_ID } from "../util/siteSchema";
import styles from "./pricingPage.module.css";

const BASE_URL = "https://shifttracker.net";

interface PlanCardProps {
  plan?: {
    name?: string;
    listPrice?: string;
    price?: string;
    period?: string;
    discount?: string;
    save?: string;
    note?: string;
  };
  planKey: "monthly" | "annual";
  contactHref: string;
  listLabel?: string;
  cta?: string;
  highlighted?: boolean;
}

function PlanCard({
  plan,
  planKey,
  contactHref,
  listLabel,
  cta,
  highlighted,
}: PlanCardProps): JSX.Element {
  return (
    <article
      className={`${styles.planCard} ${highlighted ? styles.planHighlighted : ""}`}
    >
      {plan?.discount && <span className={styles.discountBadge}>{plan.discount}</span>}
      <h2 className={styles.planName}>{plan?.name}</h2>
      <p className={styles.planPrice}>
        <strong>{plan?.price}</strong>
        <span className={styles.planPeriod}>{plan?.period}</span>
      </p>
      <p className={styles.standardPrice}>
        {listLabel}: <s>{plan?.listPrice}</s>
      </p>
      {plan?.save && <p className={styles.planSave}>{plan.save}</p>}
      <p className={styles.planNote}>{plan?.note}</p>
      <NavLink
        to={contactHref}
        className={styles.planCta}
        onClick={() => trackPricingCtaClick(planKey)}
      >
        {cta}
      </NavLink>
    </article>
  );
}

function PricingPage(): JSX.Element {
  const { data: languageData, appLanguage } = useSelector(
    (state: State) => state.languageReducer
  );
  const pricingText = languageData?.pricing;
  const isEnglish = appLanguage === "en";
  const contactHref = isEnglish ? "/en/contact/" : "/contact/";

  const features: string[] = pricingText?.features ?? [];
  const notes: string[] = pricingText?.notes ?? [];

  // Product + launch offers, so search results can surface the pricing.
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Shift Tracker",
    description: pricingText?.hero?.subtitle,
    brand: { "@type": "Organization", "@id": ORGANIZATION_ID, name: "Shift Tracker" },
    isRelatedTo: { "@id": SOFTWARE_ID },
    offers: [
      {
        "@type": "Offer",
        name: isEnglish
          ? "Monthly plan (discounted)"
          : "Aylık plan (indirimli)",
        price: String(PRODUCT_FACTS.plans.monthly.price),
        priceCurrency: PRODUCT_FACTS.currency,
        url: `${BASE_URL}${isEnglish ? "/en/pricing/" : "/fiyatlandirma/"}`,
      },
      {
        "@type": "Offer",
        name: isEnglish
          ? "Annual plan (discounted)"
          : "Yıllık plan (indirimli)",
        price: String(PRODUCT_FACTS.plans.annual.price),
        priceCurrency: PRODUCT_FACTS.currency,
        url: `${BASE_URL}${isEnglish ? "/en/pricing/" : "/fiyatlandirma/"}`,
      },
    ],
  };

  return (
    <div className={styles.page}>
      <Seo path="/fiyatlandirma" enPath="/pricing" schema={schema} />

      <section className={styles.hero}>
        <span className={styles.heroBadge}>{pricingText?.hero?.badge}</span>
        <h1 className={styles.title}>{pricingText?.hero?.title}</h1>
        <p className={styles.subtitle}>{pricingText?.hero?.subtitle}</p>
      </section>

      <section className={styles.plans}>
        <PlanCard
          plan={pricingText?.plans?.monthly}
          planKey="monthly"
          contactHref={contactHref}
          listLabel={pricingText?.listLabel}
          cta={pricingText?.cta}
        />
        <PlanCard
          plan={pricingText?.plans?.annual}
          planKey="annual"
          contactHref={contactHref}
          listLabel={pricingText?.listLabel}
          cta={pricingText?.cta}
          highlighted
        />
      </section>

      <p className={styles.notes}>{notes.join(" ")}</p>

      <section className={styles.features}>
        <h2 className={styles.featuresTitle}>{pricingText?.featuresTitle}</h2>
        <ul className={styles.featureList}>
          {features.map((feature, index) => (
            <li key={index} className={styles.featureItem}>
              <span className={styles.featureCheck} aria-hidden="true">
                ✓
              </span>
              {feature}
            </li>
          ))}
        </ul>
      </section>

      <Testimonials lang={isEnglish ? "en" : "tr"} />

      <section className={styles.bottomCta}>
        <h2>{pricingText?.bottom?.title}</h2>
        <p>{pricingText?.bottom?.subtitle}</p>
        <NavLink
          to={contactHref}
          className={styles.bottomButton}
          onClick={() => trackPricingCtaClick("bottom")}
        >
          {pricingText?.bottom?.button}
        </NavLink>
      </section>
    </div>
  );
}

export default PricingPage;
